import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCatalog } from '../hooks/useApi';
import { TopBar, Breadcrumb, ImageFB, ColorGridSkeleton, EmptyState, ErrorState, formatPrice } from '../components';

/**
 * 型号页 = 3 级结构的叶子页（品牌 → 型号 → 颜色）。
 *
 * 与旧版的差别：
 *   1. 旧版这里是「A/B/C 颜色宫格」，点进去还有第 4 级商品页；
 *      现在格子本身就是最终内容，点开只是看大图。
 *   2. 格子**不再显示任何标签**（业务方 2026-10-20 决定：只给客户看图）。
 *   3. ERP 每个 SKU 只有一张图，所以没有详情图库；单色型号改成整幅竖图，
 *      避免半宽格子显得空。
 */
export function ModelDetail({ onContact }: { onContact: () => void }) {
  const { brandId, modelId } = useParams<{ brandId: string; modelId: string }>();
  const { data, loading, error, refetch } = useCatalog();
  const [lightbox, setLightbox] = useState<string | null>(null);

  const brand = data?.brands.find((b) => b.id === brandId) ?? null;
  const model = brand?.models.find((m) => m.id === modelId) ?? null;

  const openLightbox = (url: string | null) => {
    if (url) setLightbox(url);
  };

  return (
    <div>
      <TopBar title={model?.code} showBack />
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: brand?.name || '...', to: brand ? `/brand/${brand.id}` : undefined },
        { label: model?.code || '...' },
      ]} />

      {model && (
        <div className="md-head">
          <div className="md-price-row">
            <span className="lbl">Price</span>
            <span className="val">{formatPrice(model.price)}</span>
            <span className="cur">MMK</span>
          </div>
        </div>
      )}

      {loading ? (
        <ColorGridSkeleton />
      ) : error ? (
        <ErrorState msg={error} onRetry={refetch} />
      ) : model && model.colors.length > 0 ? (
        <>
          <div className="section-title">
            <span className="ttl">Colors</span>
            <span className="rule" />
          </div>
          <div
            className="color-grid"
            style={model.colors.length === 1 ? { gridTemplateColumns: '1fr' } : undefined}
          >
            {model.colors.map((color) => (
              <div
                key={color.id}
                className="color-cell"
                style={model.colors.length === 1 ? { aspectRatio: '3 / 4' } : undefined}
                onClick={() => openLightbox(color.imageUrl)}
              >
                <div className="swatch">
                  <ImageFB src={color.imageUrl} alt={model.code} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptyState title="No colors yet" desc="Colors coming soon" />
      )}

      <div style={{ height: 96 }} />

      <button className="btn-cta" onClick={onContact}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 2C5.5 2 2 5 2 9c0 1.8.7 3.4 2 4.6L3 17l3.5-1.6c1 .3 2.2.5 3.5.5 4.5 0 8-3 8-7s-3.5-7-8-7z" fill="white" />
        </svg>
        Contact Us
      </button>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--s-4)',
          }}
        >
          <img
            src={lightbox}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain' }}
            loading="eager"
          />
        </div>
      )}
    </div>
  );
}
